/** Default sort for the System → Publishing screen (client-side only). */
export const PUBLISHING_LIST_DEFAULTS = {
  sortBy: 'courseName',
  sortDir: 'asc',
};

/** Sort keys exposed by the publishing table column headers. */
export const PUBLISHING_SORT_FIELDS = {
  courseName: 'courseName',
  ownerName: 'ownerName',
  courseType: 'courseType',
  audience: 'audience',
};

/** catalogAudienceMode values, mirroring course.catalog_audience_mode. */
export const AUDIENCE_MODE = {
  allOrgs: 0,
  selectedOrgs: 1,
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextPublishingSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || PUBLISHING_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return { sortBy, sortDir: currentSortDir === 'asc' ? 'desc' : 'asc' };
  }
  return { sortBy, sortDir: 'asc' };
}

/**
 * Short description of who can see a course in the import catalog.
 *
 * "No organizations" is a real, reachable state — a course restricted to a selected
 * list that is currently empty reaches nobody — and must read differently from
 * "All organizations" so an admin can tell the two apart at a glance.
 */
export function audienceSummary(row) {
  if (!row || Number(row.catalogAudienceMode) !== AUDIENCE_MODE.selectedOrgs) {
    return 'All organizations';
  }
  const count = (row.audienceOrgIds || []).length;
  if (count === 0) return 'No organizations';
  return `${count} organization${count === 1 ? '' : 's'}`;
}

/** Resolves a row field value for client-side column sorting. */
function publishingSortValue(row, sortBy) {
  switch (sortBy) {
    case PUBLISHING_SORT_FIELDS.ownerName:
      return row.ownerName || row.ownerEmail || '';
    case PUBLISHING_SORT_FIELDS.courseType:
      return row.courseType || '';
    case PUBLISHING_SORT_FIELDS.audience:
      return audienceSummary(row);
    case PUBLISHING_SORT_FIELDS.courseName:
    default:
      return row.courseName || '';
  }
}

/** Sorts publishing rows in memory — no API round-trip. Returns a new array. */
export function sortPublishingRowsLocally(rows, sortBy, sortDir) {
  const key = sortBy || PUBLISHING_LIST_DEFAULTS.sortBy;
  const dir = sortDir === 'desc' ? -1 : 1;
  return [...(rows || [])].sort((a, b) => {
    const left = String(publishingSortValue(a, key)).toLowerCase();
    const right = String(publishingSortValue(b, key)).toLowerCase();
    if (left < right) return -1 * dir;
    if (left > right) return 1 * dir;
    return 0;
  });
}

/** Narrows rows by free-text course name and/or a specific owner account. */
export function filterPublishingRows(rows, { query, ownerUserId } = {}) {
  const needle = (query || '').trim().toLowerCase();
  return (rows || []).filter((row) => {
    if (ownerUserId && row.ownerUserId !== ownerUserId) return false;
    if (!needle) return true;
    return String(row.courseName || '').toLowerCase().includes(needle);
  });
}

/**
 * Applies a visibility change to the local rows for optimistic rendering.
 * An omitted flag is left untouched, matching the API's null-means-unchanged rule.
 */
export function applyVisibilityLocally(rows, courseIds, { catalogVisible, storeVisible } = {}) {
  const targets = new Set(courseIds || []);
  return (rows || []).map((row) => {
    if (!targets.has(row.courseId)) return row;
    const next = { ...row };
    if (catalogVisible !== undefined && catalogVisible !== null) next.catalogVisible = catalogVisible;
    if (storeVisible !== undefined && storeVisible !== null) next.storeVisible = storeVisible;
    return next;
  });
}

/**
 * Applies an audience change to one row for optimistic rendering.
 * Switching back to "all organizations" keeps the previously chosen orgIds on the row
 * so reopening the dialog shows the earlier selection, matching the server, which
 * deactivates rather than deletes those rows.
 */
export function applyAudienceLocally(rows, courseId, { mode, orgIds } = {}) {
  return (rows || []).map((row) => {
    if (row.courseId !== courseId) return row;
    const next = { ...row, catalogAudienceMode: mode };
    if (mode === AUDIENCE_MODE.selectedOrgs) {
      next.audienceOrgIds = [...(orgIds || [])];
    }
    return next;
  });
}
